import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { trackAuthEvent } from '../utils/analytics';
import { apiService, SignupData } from '../services/api';

interface User {
  userId: number;
  role: string;
  phone: string;
  email?: string;
  firstName?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (phone: string, password: string) => Promise<void>;
  loginWithOTP: (phone: string, otp: string) => Promise<void>;
  requestOTP: (phone: string) => Promise<any>;
  resendOTP: (phone: string) => Promise<any>;
  signup: (userData: SignupData) => Promise<any>;
  verifyRegistrationOTP: (phone: string, otp: string) => Promise<any>;
  forgotPassword: (email?: string, phone?: string) => Promise<any>;
  resetPassword: (phone: string, otpCode: string, newPassword: string) => Promise<any>;
  logout: () => void;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  isCommittee: boolean;
  isVendor: boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  // During development with hot reloading, we might encounter issues where the context is undefined
  // This fallback helps prevent crashes during development
  if (import.meta.env.DEV && !context) {
    console.error('useAuth was called outside of AuthProvider. Component tree might be corrupted due to hot reload.');
    console.warn('Returning fallback auth context. Please refresh the page if you encounter issues.');
    
    // Return a fallback context with empty implementations
    return {
      user: null,
      token: null,
      login: async () => { console.error('Auth context not properly initialized'); },
      loginWithOTP: async () => { console.error('Auth context not properly initialized'); },
      requestOTP: async () => { console.error('Auth context not properly initialized'); },
      resendOTP: async () => { console.error('Auth context not properly initialized'); },
      signup: async () => { console.error('Auth context not properly initialized'); },
      verifyRegistrationOTP: async () => { console.error('Auth context not properly initialized'); },
      forgotPassword: async () => { console.error('Auth context not properly initialized'); },
      resetPassword: async () => { console.error('Auth context not properly initialized'); },
      logout: () => { console.error('Auth context not properly initialized'); },
      loading: false,
      error: 'Auth context not properly initialized',
      isAdmin: false,
      isCommittee: false,
      isVendor: false,
      clearError: () => { console.error('Auth context not properly initialized'); },
    } as AuthContextType;
  }
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider. If you see this during development, try refreshing the page.');
  }
  
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for existing token on app load
  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    const storedUser = sessionStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = async (phone: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.login({ phone, password });

      // Store token and user data in sessionStorage
      sessionStorage.setItem('token', response.token || '');
      sessionStorage.setItem('user', JSON.stringify({
        userId: response.userId || 0,
        role: response.role || '',
        phone: phone,
        firstName: response.firstName || undefined,
      }));

      setToken(response.token || null);
      setUser({
        userId: response.userId || 0,
        role: response.role || '',
        phone: phone,
        firstName: response.firstName || undefined,
      });
      
      // Track login event
      trackAuthEvent('login', 'password');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithOTP = async (phone: string, otp: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.verifyOTP(phone, otp, 'otp');

      // Store token and user data in sessionStorage
      sessionStorage.setItem('token', response.token || '');
      sessionStorage.setItem('user', JSON.stringify({
        userId: response.userId || 0,
        role: response.role || '',
        phone: phone,
        firstName: response.firstName || undefined,
      }));

      setToken(response.token || null);
      setUser({
        userId: response.userId || 0,
        role: response.role || '',
        phone: phone,
        firstName: response.firstName || undefined,
      });
      
      // Track OTP login event
      trackAuthEvent('login', 'otp');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'OTP verification failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const requestOTP = async (phone: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.requestOTP(phone);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send OTP';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async (phone: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.resendOTP(phone);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend OTP';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData: SignupData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.signup(userData);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyRegistrationOTP = async (phone: string, otp: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.verifyOTP(phone, otp, 'password');
      // For registration OTP, we don't want to automatically log the user in
      // Just return the response to confirm the registration is complete
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'OTP verification failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email?: string, phone?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.forgotPassword(email, phone);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send password reset';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (phone: string, _otpCode: string, newPassword: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.resetPassword(phone, newPassword);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setError(null);
    
    // Track logout event
    trackAuthEvent('logout');
  };

  const clearError = () => {
    setError(null);
  };

  // Computed properties for role checking
  const isAdmin = user?.role === 'ADMIN';
  const isCommittee = user?.role === 'COMMITTEE' || user?.role === 'COMMITTEE_ADMIN';
  const isVendor = user?.role === 'VENDOR' || user?.role === 'VENDOR_ADMIN';

  const value = {
    user,
    token,
    login,
    loginWithOTP,
    requestOTP,
    resendOTP,
    signup,
    verifyRegistrationOTP,
    forgotPassword,
    resetPassword,
    logout,
    loading,
    error,
    isAdmin,
    isCommittee,
    isVendor,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 